import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import SEO from "@/components/SEO";
import CategoryCarousel from "@/components/public/CategoryCarousel";
import AdSlot from "@/components/public/AdSlot";

const DEFAULT_VIDEO = "https://assets.mixkit.co/videos/1087/1087-720.mp4";
const DEFAULT_POSTER = "https://images.unsplash.com/photo-1541417904950-b855846fe074?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";
const DEFAULT_AUDIO = "https://actions.google.com/sounds/v1/water/waves_crashing_on_rock_beach.ogg";

export default function CategoriesPage() {
  const [cats, setCats] = useState([]);
  const { settings } = useSite();
  const audioRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);

  useEffect(() => { api.get("/categories?active_only=true").then((r) => setCats(r.data || [])).catch(() => {}); }, []);

  const videoSrc = mediaUrl(settings.categories_bg_video) || DEFAULT_VIDEO;
  const posterSrc = mediaUrl(settings.categories_bg_poster) || DEFAULT_POSTER;
  const audioSrc = mediaUrl(settings.categories_bg_audio) || DEFAULT_AUDIO;

  const toggleSound = () => {
    const a = audioRef.current;
    if (!a) return;
    if (soundOn) { a.pause(); setSoundOn(false); }
    else { a.volume = 0.35; a.play().then(() => setSoundOn(true)).catch(() => setSoundOn(false)); }
  };

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0a2233] text-white" data-testid="categories-page">
      <SEO title="Categorías" description="Explora nuestras secciones editoriales: escapadas, gastronomía, moda, tecnología, cultura y más." />
      <audio ref={audioRef} src={audioSrc} loop preload="none" data-testid="waves-audio" />

      {/* Beach background: absolute layer with CSS poster fallback + video on top */}
      <div
        className="absolute inset-0 z-0 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url('${posterSrc}')` }}
      >
        <video
          key={videoSrc}
          autoPlay muted loop playsInline poster={posterSrc}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="beach-video-bg"
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a2233]/45 via-[#0a2233]/35 to-[#0a2233]/75" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-cyan-300/20 blur-3xl animate-drift" />
        <div className="absolute top-1/3 -right-24 w-[28rem] h-[28rem] rounded-full bg-sunset-accent/20 blur-3xl animate-drift" style={{ animationDelay: "5s" }} />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 rounded-full bg-white/10 blur-3xl animate-drift" style={{ animationDelay: "9s" }} />
      </div>

      {/* Sound toggle */}
      <button
        onClick={toggleSound}
        data-testid="sound-toggle"
        aria-label={soundOn ? "Silenciar olas" : "Activar sonido de olas"}
        className="fixed bottom-6 right-6 z-[120] flex items-center gap-2 backdrop-blur-md bg-white/15 border border-white/30 text-white px-4 py-3 rounded-full shadow-2xl hover:bg-white/25 transition-colors"
      >
        {soundOn ? <Volume2 size={18} className="text-cyan-200" /> : <VolumeX size={18} />}
        <span className="kicker text-[10px] hidden sm:inline">{soundOn ? "Sonido de olas" : "Activar olas"}</span>
        {soundOn && <span className="flex items-end gap-0.5 h-3">
          <span className="w-0.5 bg-cyan-200 animate-pulse" style={{ height: "60%" }} />
          <span className="w-0.5 bg-cyan-200 animate-pulse" style={{ height: "100%", animationDelay: "0.2s" }} />
          <span className="w-0.5 bg-cyan-200 animate-pulse" style={{ height: "40%", animationDelay: "0.4s" }} />
        </span>}
      </button>

      <div className="relative z-10 pt-32 md:pt-44 pb-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
            className="max-w-2xl backdrop-blur-md bg-white/10 border border-white/20 rounded-sm p-8 md:p-10 shadow-2xl animate-floaty"
          >
            <p className="kicker text-cyan-200 mb-4">Explora por sección</p>
            <h1 className="font-serif text-white text-5xl md:text-7xl tracking-tight leading-[0.95]">Categorías</h1>
            <p className="text-white/80 mt-5 leading-relaxed">Desliza hacia los lados para recorrer nuestras secciones. Cada una es una ventana a un mundo de historias.</p>
          </motion.div>
        </div>

        <div className="max-w-[1400px] mx-auto pl-5 md:pl-10 mt-14 md:mt-20">
          <CategoryCarousel categories={cats} variant="glass" autoplay />
        </div>

        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-16 md:mt-24">
          <AdSlot position="Categorías" />
        </div>
      </div>
    </div>
  );
}
